import { generateTreeData } from "components/TemplateEditor/Functions";
import { useField } from "formik";
import { isArray } from "lodash";
import React, { Key, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  $Button,
  $Empty,
  $Input,
  $Popconfirm,
  $Skeleton,
  $Tooltip,
  $Tree,
} from "us.common/components";
import { RootState } from "us.helper/types";
import { PlusOutlined, SearchOutlined } from "us.icons";

type TabContentProps = {
  data: any[];
  adapterName?: string;
  isLoading: boolean;
  isRepeating?: boolean;
  disabledAddRepeatingAdapter?: boolean;
  disabled?: boolean;
  onSelectToken?: (
    tokenName: string,
    isRepeating: boolean,
    adapterId?: number
  ) => void;
  onClickAddRepeatingAdapter?: () => void;
  onSelectAdapter?: (adapterId: number) => void;
};

const TabContent: React.FC<TabContentProps> = (props) => {
  const {
    data,
    isRepeating,
    isLoading,
    adapterName = "",
    disabledAddRepeatingAdapter,
    disabled = false,
    onClickAddRepeatingAdapter,
    onSelectToken,
    onSelectAdapter,
  } = props;
  const { t } = useTranslation();
  const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([]);

  const section = useSelector(
    (state: RootState) => state.templateEditor.section
  );
  const [searchField] = useField("searchText");
  const searchValue = searchField.value;

  useEffect(() => {
    if (section && section.repeatingAdapterId) {
      setSelectedKeys([section.repeatingAdapterId]);
    } else {
      setSelectedKeys([]);
    }
  }, [section]);

  const treeData = useMemo(() => {
    if (isRepeating) {
      return generateTreeData(data, searchValue);
    }
  }, [searchValue, data, isRepeating]);

  return (
    <>
      <div className="search-params d-flex px-2 py-2 mb-2 mt-n3">
        <div className="flex-grow-1">
          <$Input
            name="searchText"
            placeholder={`${t(
              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.SEARCH_PARAMETERS"
            )}`}
            prefix={<SearchOutlined className="text-muted" />}
            size="small"
            bordered={false}
            allowClear={true}
            className="mb-0"
          />
        </div>
        {isRepeating && !disabledAddRepeatingAdapter && (
          <$Tooltip
            title={t(
              "US.USC.TEMPLATEEDITOR:TEMPLATE_EDITOR.ADD_NEW_REPEATING_ADAPTER"
            )}
            placement="topLeft"
          >
            <$Button
              onClick={onClickAddRepeatingAdapter}
              type="link"
              icon={<PlusOutlined />}
              className="mb-0 ml-2"
              disabled={disabled}
            />
          </$Tooltip>
        )}
      </div>
      {!isRepeating && (
        <div className="property-content">
          <div className="p-2 pb-4">
            <$Skeleton
              loading={isLoading}
              active
              paragraph={{
                rows: 2,
              }}
            >
              {isArray(data) && data.length > 0 && (
                <h4 className="py-1 ml-2">{adapterName}</h4>
              )}
              {isArray(data) &&
                data.map(
                  ({ tokenName }: { tokenName: string }, index: number) => {
                    return (
                      <div
                        className="d-flex ml-1  py-1 px-2 mb-1 editor-list-item"
                        key={tokenName + index}
                        onDoubleClick={() =>
                          onSelectToken && onSelectToken(tokenName, false)
                        }
                      >
                        <div className="d-flex align-items-center text-muted">
                          {tokenName}
                        </div>
                      </div>
                    );
                  }
                )}
            </$Skeleton>
            {isArray(data) && data.length == 0 && !isLoading && (
              <$Empty image={$Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        </div>
      )}
      {isRepeating && (
        <div className="property-content">
          <div className="p-2 pb-4">
            <$Skeleton
              loading={isLoading}
              active
              paragraph={{
                rows: 2,
              }}
            >
              <$Tree
                checkable
                treeData={treeData}
                fieldNames={{
                  title: "tokenName",
                  key: "tokenValue",
                  children: "dataTokens",
                }}
                expandedKeys={selectedKeys}
                checkedKeys={
                  section.repeatingAdapterId ? [section.repeatingAdapterId] : []
                }
                onExpand={(expandedKeys: Key[]) => {
                  setSelectedKeys(expandedKeys);
                }}
                onDoubleClick={(e) =>
                  onSelectToken &&
                  onSelectToken(
                    e.currentTarget.innerText,
                    true,
                    parseInt(selectedKeys[0].toString())
                  )
                }
                onCheck={(checked: any, info: any) => {
                  if (info.node.adapterId != section.repeatingAdapterId) {
                    setCheckedKeys([info.node.adapterId]);
                  }
                }}
                titleRender={(adapter: any) => {
                  if (adapter.dataTokens) {
                    return (
                      <$Popconfirm
                        title={t(
                          "US.USC.TEMPLATEEDITOR:MESSAGES.ADAPTER_CHANGE_MAY_AFFECT_THE_AVAILABLE_TOKENS_IN_THE_SECTION_PLEASE_CHECK_AND_PROCEED"
                        )}
                        visible={checkedKeys.includes(adapter.adapterId)}
                        placement="topLeft"
                        onConfirm={() => {
                          setCheckedKeys([adapter.adapterId]);
                          onSelectAdapter && onSelectAdapter(adapter.adapterId);
                        }}
                        onVisibleChange={(visible: any) => {
                          if (!visible) {
                            setCheckedKeys([]);
                          }
                        }}
                      >
                        <div className="d-flex align-items-center text-muted">
                          {adapter.tokenName}
                        </div>
                      </$Popconfirm>
                    );
                  } else {
                    return (
                      <div className="d-flex align-items-center text-muted">
                        {adapter.tokenName}
                      </div>
                    );
                  }
                }}
              />
              {isArray(treeData) && treeData.length == 0 && !isLoading && (
                <$Empty image={$Empty.PRESENTED_IMAGE_SIMPLE} />
              )}
            </$Skeleton>
          </div>
        </div>
      )}
    </>
  );
};

export default TabContent;
